import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUser } from '../services/userService';
import { getUnreadNotificationCount } from '../services/notificationService';
import { logout as logoutRequest } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const logoutRef = useRef(async () => {});

  logoutRef.current = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        await logoutRequest();
      } catch {
        /* server revoke best-effort */
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    setUnreadNotificationCount(0);
  };

  const logout = useCallback(() => logoutRef.current(), []);

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    try {
      const stored = JSON.parse(localStorage.getItem('user'));
      if (!stored?.id) { await logoutRef.current(); return; }
      const { data } = await getUser(stored.id);
      setUser(data.data);
      localStorage.setItem('user', JSON.stringify(data.data));
    } catch {
      await logoutRef.current();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    if (loading || !user?.id) {
      if (!user?.id) setUnreadNotificationCount(0);
      return undefined;
    }

    let cancelled = false;
    getUnreadNotificationCount()
      .then(({ data }) => {
        if (!cancelled) {
          setUnreadNotificationCount(data?.data?.count ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) setUnreadNotificationCount(0);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, loading]);

  const loginSuccess = (token, userData) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const refreshUser = fetchProfile;

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      loginSuccess,
      logout,
      refreshUser,
      unreadNotificationCount,
      setUnreadNotificationCount,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
